import React, { FC } from "react"
import { Link } from "gatsby"

import { PostTemplatePostFragmentFragment } from "@graphqlTypes"

import { oneLineHelpScout } from "@utils"

import PostAuthors from "../PostHeroBlog/components/PostAuthors/PostAuthors"
import Badge, { BADGE_COLORS } from "@components/Badge/Badge"
import Media from "@components/lp/Media/Media"

import { PostHeroCustomerStoryHERO } from "./PostHeroCustomerStory.styles"

type FeatureUrls = {
  [key: string]: string
}

const FEATURE_URLS: FeatureUrls = {
  Beacon: "/live-chat",
  "In-app Messaging": "/proactive-messages/",
  Integrations: "/help-desk-integration/",
  "Knowledge Base": "/knowledge-base/",
  "Live Chat": "/live-chat",
  Reporting: "/analytics/",
  "Shared Inbox": "/shared-inbox/",
  Workflows: "/help-desk-workflows/",
}

export interface PostHeroCustomerStoryProps {
  authors: PostTemplatePostFragmentFragment["authors"]
  name: string
  date: PostTemplatePostFragmentFragment["date"]
  heroCompanySize?: number
  heroFeatures?: [string]
  heroIndustry?: string
  image: PostTemplatePostFragmentFragment["image"]
  title: string
}

const PostHeroCustomerStory: FC<PostHeroCustomerStoryProps> = ({
  authors,
  date,
  heroCompanySize,
  heroFeatures,
  heroIndustry,
  image,
  title,
}) => (
  <PostHeroCustomerStoryHERO>
    <div className="container">
      <div className="grid">
        <div className="column column--1-7-XS column--1-13-M column--2-12-L">
          <div className="PostHeroCustomerStoryHero--content">
            <Badge color={BADGE_COLORS.CHARCOAL_TINT}>Customer Story</Badge>
            <h1
              dangerouslySetInnerHTML={{
                __html: oneLineHelpScout(title),
              }}
            />

            <PostAuthors authors={authors} date={date} />
            {image && <Media isAboveFold image={image} />}
            {(heroCompanySize || heroFeatures || heroIndustry) && (
              <div className="PostHeroCustomerStoryHero--stats">
                <dl className="grid">
                  {heroCompanySize && (
                    <div
                      data-name="company-size"
                      className="column column--1-7-XS column--1-4-M padding-left--base-1-25"
                    >
                      <dt className="type--mono-XS">Company Size</dt>
                      <dd className="color--text-headline type--serif-3XL margin-bottom--0">
                        {heroCompanySize}
                      </dd>
                    </div>
                  )}
                  {heroIndustry && (
                    <div
                      data-name="industry"
                      className="column column--1-7-XS column--4-7-M padding-left--base-1-25"
                    >
                      <dt className="type--mono-XS">Industry</dt>
                      <dd className="color--text-headline type--serif-3XL margin-bottom--0">
                        {heroIndustry}
                      </dd>
                    </div>
                  )}
                  {heroFeatures && (
                    <div
                      data-name="features"
                      className="column column--1-7-XS column--1-13-M padding-left--base-1-25"
                    >
                      <dt className="type--mono-XS">Favorite features</dt>
                      <dd className="color--text-headline type--text-XL font-weight--light margin-bottom--0">
                        {(heroFeatures as Array<string>).map(
                          (feature, i, array) => (
                            <span>
                              <React.Fragment key={feature}>
                                <Link
                                  to={FEATURE_URLS[feature]}
                                  className="color--text-headline"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  {feature}
                                </Link>
                              </React.Fragment>
                              {i !== array.length - 1 ? ", " : ""}
                            </span>
                          )
                        )}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </PostHeroCustomerStoryHERO>
)

export default PostHeroCustomerStory
